import {useState} from 'react';
import {
    Box,
    Button,
    ButtonBase,
    Drawer,
    IconButton,
    Snackbar,
    Alert,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {BugReport as BugIcon, Lightbulb as FeatureIcon, ChatBubbleOutlined as GeneralIcon} from '@mui/icons-material';
import apiClient from '../api/client';

type FeedbackType = 'bug' | 'feature' | 'feedback';

export default function FeedbackWidget() {
    const [open, setOpen] = useState(false);
    const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<{open: boolean; severity: 'success' | 'error'; text: string}>({
        open: false, severity: 'success', text: '',
    });

    const resetForm = () => {
        setFeedbackType('bug');
        setMessage('');
    };

    const handleSubmit = async () => {
        if (!message.trim()) return;
        setSubmitting(true);
        try {
            const sessionURL = (window as any).__openReplayTracker?.getSessionURL?.() || null;
            await apiClient.submitFeedback({
                feedback_type: feedbackType,
                message: message.trim(),
                page_url: window.location.href,
                browser_info: navigator.userAgent,
                openreplay_url: sessionURL,
            });
            setSnackbar({open: true, severity: 'success', text: 'Thank you for your feedback!'});
            resetForm();
            setOpen(false);
        } catch {
            setSnackbar({open: true, severity: 'error', text: 'Failed to send feedback. Please try again.'});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {/* Desktop: Vertical edge tab — fixed to right side */}
            <ButtonBase
                onClick={() => setOpen(true)}
                sx={{
                    display: {xs: 'none', md: 'flex'},
                    position: 'fixed',
                    right: 0,
                    bottom: 80,
                    zIndex: 1200,
                    transform: 'rotate(180deg)',
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    px: 0.75,
                    py: 1.5,
                    borderRadius: '4px 0 0 4px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {bgcolor: 'primary.dark'},
                }}
                aria-label="Send feedback"
            >
                FEEDBACK
            </ButtonBase>
            {/* Mobile: FAB in bottom-right corner */}
            <IconButton
                onClick={() => setOpen(true)}
                sx={{
                    display: {xs: 'flex', md: 'none'},
                    position: 'fixed',
                    right: 16,
                    bottom: 16,
                    zIndex: 1200,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    width: 48,
                    height: 48,
                    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {bgcolor: 'primary.dark'},
                }}
                aria-label="Send feedback"
            >
                <GeneralIcon/>
            </IconButton>
            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                slotProps={{
                    paper: {sx: {width: {xs: '100%', sm: 360}, p: 3}}
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Send Feedback
                </Typography>

                <ToggleButtonGroup
                    value={feedbackType}
                    exclusive
                    onChange={(_, v) => v && setFeedbackType(v)}
                    size="small"
                    fullWidth
                    sx={{mb: 2}}
                >
                    <ToggleButton value="bug" sx={{gap: 0.5}}>
                        <BugIcon fontSize="small"/> Bug
                    </ToggleButton>
                    <ToggleButton value="feature" sx={{gap: 0.5}}>
                        <FeatureIcon fontSize="small"/> Feature
                    </ToggleButton>
                    <ToggleButton value="feedback" sx={{gap: 0.5}}>
                        <GeneralIcon fontSize="small"/> General
                    </ToggleButton>
                </ToggleButtonGroup>

                <TextField
                    label="Describe your feedback"
                    multiline
                    rows={4}
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{mb: 2}}
                />

                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!message.trim() || submitting}
                    >
                        {submitting ? 'Sending...' : 'Send'}
                    </Button>
                </Box>
            </Drawer>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({...s, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({...s, open: false}))}
                >
                    {snackbar.text}
                </Alert>
            </Snackbar>
        </>
    );
}
